import { useEffect, useMemo, useState } from "react";

import {
  ATTACHMENT_SIGNED_URL_TTL_SECONDS,
  ATTACHMENTS_BUCKET,
  getAttachmentTarget,
} from "../providers/commons/attachments";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Reuse a signed URL for most of its validity instead of signing per render.
const CACHE_MS = ATTACHMENT_SIGNED_URL_TTL_SECONDS * 1000 * 0.8;
const signedUrlCache = new Map<
  string,
  { promise: Promise<string | null>; at: number }
>();

const signAttachmentPath = (path: string): Promise<string | null> => {
  const cached = signedUrlCache.get(path);
  if (cached && Date.now() - cached.at < CACHE_MS) {
    return cached.promise;
  }
  const promise = Promise.resolve(
    getSupabaseClient()
      .storage.from(ATTACHMENTS_BUCKET)
      .createSignedUrl(path, ATTACHMENT_SIGNED_URL_TTL_SECONDS),
  )
    .then(({ data, error }) => {
      if (error || !data?.signedUrl) {
        signedUrlCache.delete(path);
        return null;
      }
      return data.signedUrl;
    })
    .catch(() => {
      signedUrlCache.delete(path);
      return null;
    });
  signedUrlCache.set(path, { promise, at: Date.now() });
  return promise;
};

/**
 * Resolves a stored attachment to a browser-openable URL, lazily signing
 * storage paths and legacy public URLs. Never throws: a signing failure
 * yields `status: "failed"` so callers can show just the file name.
 */
export const useAttachmentUrl = (
  src: string | undefined,
  path?: string,
): { url: string | null; status: "ready" | "pending" | "failed" } => {
  const target = useMemo(() => getAttachmentTarget(src, path), [src, path]);
  const storagePath = target?.kind === "storage" ? target.path : null;
  const [signed, setSigned] = useState<{
    path: string;
    url: string | null;
  } | null>(null);

  useEffect(() => {
    if (!storagePath) return;
    let cancelled = false;
    signAttachmentPath(storagePath).then((url) => {
      if (!cancelled) setSigned({ path: storagePath, url });
    });
    return () => {
      cancelled = true;
    };
  }, [storagePath]);

  if (!target) return { url: null, status: "failed" };
  if (target.kind === "direct") return { url: target.url, status: "ready" };
  if (signed?.path !== storagePath) return { url: null, status: "pending" };
  return signed.url
    ? { url: signed.url, status: "ready" }
    : { url: null, status: "failed" };
};
